'use client';

import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import AnimateOnScroll from '@/components/AnimateOnScroll';
import GridSection from '@/components/site/GridSection';
import GridRule from '@/components/site/GridRule';
import { useFormGuard } from '@/components/forms/useFormGuard';
import { HUMAN_CHECK_FAILED, useHumanCheck } from '@/components/forms/useHumanCheck';
import { changelogStats } from './data';

/** Same masked line-rise as the pricing hero (shared .pricing-hero-line CSS —
 *  the rule is page-agnostic; under reduced motion it never applies, so the
 *  text can't strand hidden). */
function HeroLine({ index, children }: { index: number; children: React.ReactNode }) {
  return (
    <span className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
      <span
        className="pricing-hero-line block"
        style={{ animationDelay: `${120 + index * 110}ms` }}
      >
        {children}
      </span>
    </span>
  );
}

function StatChip({
  children,
  tone = 'default',
}: {
  children: React.ReactNode;
  tone?: 'default' | 'green' | 'dim' | 'amber';
}) {
  const color =
    tone === 'green'
      ? 'text-[color-mix(in_srgb,var(--color-data-green)_55%,var(--color-ink))]'
      : tone === 'amber'
        ? 'text-[color-mix(in_srgb,var(--color-data-amber)_60%,var(--color-ink))]'
        : tone === 'dim'
          ? 'text-dim'
          : 'text-ink';
  return (
    <span
      className={`rounded-full border border-line-2 bg-surface px-3 py-[7px] font-jetbrains text-[11px] tracking-[0.08em] ${color}`}
    >
      {children}
    </span>
  );
}

/** RSS link + email capture, one dock. Submitting only mints and mails a
 *  confirmation token (POST /api/changelog-subscribe) — the operator hears
 *  nothing until the subscriber confirms via /changelog/confirm, per the
 *  double opt-in flow in /api/changelog-confirm. */
function SubscribeDock() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  // The generic dock copy ("That didn't send — try again?") hides a fail-
  // closed message that names an actual way through (e.g. when Turnstile is
  // unconfigured, lib/abuse.ts's 403 body says "Please email
  // support@rii.link directly"). Surface it when the server sends one.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { guardFields, HoneypotField } = useFormGuard();
  const human = useHumanCheck();

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (state === 'sending') return;
    const payload = { email, ...guardFields() };
    setState('sending');
    setErrorMessage(null);
    try {
      // Every submission needs a fresh Turnstile token (website#302).
      const turnstileToken = await human.getToken();
      if (!turnstileToken) {
        setErrorMessage(HUMAN_CHECK_FAILED);
        setState('error');
        return;
      }
      const res = await fetch('/api/changelog-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, turnstileToken }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setState('done');
      } else {
        setErrorMessage(typeof body.error === 'string' ? body.error : null);
        setState('error');
      }
    } catch {
      setState('error');
    } finally {
      human.reset();
    }
  };

  return (
    <div className="mt-[30px] flex flex-wrap items-center gap-x-7 gap-y-4 rounded-ref border border-line bg-surface px-5 py-3.5 shadow-ref">
      <a
        href="/changelog/feed.xml"
        className="inline-flex items-center gap-[9px] text-[13.5px] font-semibold text-ink transition-colors hover:text-brand-600"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M4 11a9 9 0 0 1 9 9M4 4a16 16 0 0 1 16 16"
            stroke="var(--color-brand-600)"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
          <circle cx="5.5" cy="18.5" r="1.8" fill="var(--color-brand-600)" />
        </svg>
        RSS &mdash; /changelog/feed.xml
      </a>
      <span aria-hidden className="hidden h-5 w-px bg-line-2 sm:block" />
      {state === 'done' ? (
        <span className="inline-flex items-center gap-2 text-[13.5px] text-muted" role="status">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M5 12.5 10 17.5 19 7"
              stroke="var(--color-data-green)"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Almost there &mdash; check your inbox and confirm.
        </span>
      ) : (
        <form onSubmit={submit} onFocus={human.arm} className="flex min-w-[260px] flex-1 flex-wrap items-center gap-2.5">
          <HoneypotField />
          <input
            type="email"
            required
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder="you@company.com"
            aria-label="Email address"
            className="min-w-[160px] max-w-[280px] flex-1 rounded-full border border-line-2 bg-bg-warm px-3.5 py-2 text-[13.5px] text-ink outline-brand-600 placeholder:text-dim"
          />
          <button
            type="submit"
            disabled={state === 'sending'}
            className="rounded-full bg-brand-600 px-[18px] py-2 text-[13.5px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
          >
            {state === 'sending' ? 'Sending…' : 'Notify me'}
          </button>
          <span className="text-[12.5px] text-dim" aria-live="polite">
            {state === 'error'
              ? errorMessage || 'That didn’t send — try again?'
              : 'Get notified when something ships.'}
          </span>
        </form>
      )}
      {human.widget && <div className="w-full empty:hidden">{human.widget}</div>}
    </div>
  );
}

/** Editorial hero: mono eyebrow, oversized masked headline, then an info row
 *  (premise lead → derived stat chips) and the subscribe dock. The stat chips
 *  are computed from the data module — the day "0 shipped late" stops being
 *  true, the chip says so on its own. */
export default function ChangelogHero() {
  const stats = changelogStats();

  return (
    <GridSection className="pt-[calc(var(--grid-cell,72px)*2.5)] pb-16">
      <span className="block font-jetbrains text-[11px] font-semibold tracking-[0.16em] text-brand-600">
        CHANGELOG &middot; RELEASE HISTORY
      </span>
      <h1 className="mt-5 font-onest text-[clamp(40px,5.8vw,88px)] font-extrabold leading-[1.0] tracking-[-0.045em] text-ink">
        <HeroLine index={0}>What shipped &mdash;</HeroLine>
        <HeroLine index={1}>
          and what we promised<span className="pricing-hero-dot text-brand-600">.</span>
        </HeroLine>
      </h1>

      <AnimateOnScroll animation="fade-up" delay={120}>
        <div className="mt-11 flex flex-col gap-6 md:flex-row md:items-center md:gap-8">
          <p className="max-w-[480px] text-[16.5px] leading-[1.55] text-muted">
            Every dated roadmap tag on this site ends up here &mdash; either
            still ticking, or already shipped. Real dates, both directions.
          </p>
          <ArrowRight size={22} className="hidden shrink-0 text-brand-600 md:block" aria-hidden />
          <div className="flex flex-wrap gap-2 md:ml-auto">
            <StatChip>
              {stats.releases} RELEASES IN {stats.releasesYear}
            </StatChip>
            <StatChip tone="green">
              {stats.kept} {stats.kept === 1 ? 'PROMISE' : 'PROMISES'} KEPT
            </StatChip>
            <StatChip tone={stats.late > 0 ? 'amber' : 'dim'}>
              {stats.late} SHIPPED LATE
            </StatChip>
          </div>
        </div>
        <SubscribeDock />
        <GridRule />
      </AnimateOnScroll>
    </GridSection>
  );
}
